import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Share2, Copy, X } from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { api, photoSrc, shareOrCopy, copyLinkOnly } from "../lib/api";

export default function PrintCase() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/cases/${code}`).then((res) => setC(res.data)).catch(() => {});
  }, [code]);

  if (!c) return <div className="text-[#475569]">Cargando…</div>;
  const src = photoSrc(c.photo_url);
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Sin nombre";
  const caseUrl = `${window.location.origin}/caso/${c.public_case_code}`;
  const copyLink = () => copyLinkOnly(caseUrl, () => toast.success("Enlace copiado"));
  const shareText = `Reencuentro · Ficha pública: ${c.public_case_code}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Reencuentro", text: shareText, url: caseUrl }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    setShareOpen(true);
  };
  const enc = encodeURIComponent;
  const shareLinks = [
    { label: "Copiar enlace", action: () => { copyLink(); setShareOpen(false); } },
    { label: "Email", href: `mailto:?subject=${enc("Reencuentro")}&body=${enc(shareText + " " + caseUrl)}` },
    { label: "SMS / mensaje", href: `sms:?&body=${enc(shareText + " " + caseUrl)}` },
    { label: "WhatsApp", href: `https://wa.me/?text=${enc(shareText + " " + caseUrl)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(caseUrl)}` },
    { label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(caseUrl)}` },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="no-print mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button onClick={() => navigate(-1)} data-testid="print-back-btn"
          className="flex items-center justify-center gap-1.5 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium"><ArrowLeft className="w-4 h-4" /> Volver</button>
        <button onClick={() => window.print()} data-testid="print-btn"
          className="flex items-center justify-center gap-1.5 h-12 rounded-xl bg-[#1E3A8A] text-white font-semibold"><Printer className="w-4 h-4" /> Imprimir</button>
        <button onClick={share} data-testid="print-share-btn"
          className="flex items-center justify-center gap-1.5 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium"><Share2 className="w-4 h-4" /> Compartir</button>
        <button onClick={copyLink} data-testid="print-copy-btn"
          className="flex items-center justify-center gap-1.5 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium"><Copy className="w-4 h-4" /> Copiar enlace</button>
      </div>

      <div className="bg-white border-2 border-[#0F172A] rounded-lg p-6 space-y-5">
        <div className="text-center border-b-2 border-[#0F172A] pb-3">
          <h1 className="font-heading text-3xl font-bold">{c.case_type === "missing" ? "PERSONA DESAPARECIDA" : "PERSONA ENCONTRADA"}</h1>
          <p className="text-sm text-[#475569]">Reencuentro · Ayuda humanitaria</p>
        </div>

        <div className="flex gap-5">
          <div className="w-40 h-48 bg-[#F1F5F9] shrink-0 flex items-center justify-center overflow-hidden border border-[#E2E8F0]">
            {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> : <span className="text-[#94A3B8]">Sin foto</span>}
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="font-heading text-2xl font-bold">{name}</h2>
            <p><strong>Edad aprox.:</strong> {c.approximate_age != null ? `${c.approximate_age} años` : (c.age_range || "—")}</p>
            <p><strong>Zona aprox.:</strong> {c.approximate_zone || "—"}</p>
            <p><strong>Fecha aprox.:</strong> {c.approximate_date || "—"}</p>
            {c.clothing_description && <p><strong>Ropa:</strong> {c.clothing_description}</p>}
            {c.distinguishing_marks && <p><strong>Señales:</strong> {c.distinguishing_marks}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between border-t-2 border-[#0F172A] pt-4">
          <div>
            <p className="text-sm uppercase font-semibold text-[#475569]">Código de caso</p>
            <p className="font-mono text-2xl font-bold">{c.public_case_code}</p>
            <p className="text-sm text-[#475569] mt-2 max-w-xs">Escanea el QR o visita el código para reportar información de forma privada y segura.</p>
          </div>
          <div className="p-2 border border-[#0F172A]">
            <QRCode value={caseUrl} size={110} />
          </div>
        </div>
      </div>

      {shareOpen && (
        <div className="no-print fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="share-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShareOpen(false)} />
          <div className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl p-5 space-y-3 m-0 sm:m-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold">Compartir ficha pública</h3>
              <button onClick={() => setShareOpen(false)} data-testid="share-modal-close"
                className="text-[#475569] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-[#94A3B8]">Solo se comparte el enlace público y limitado del caso. Nunca se incluyen datos privados.</p>
            <div className="grid grid-cols-1 gap-2">
              {shareLinks.map((s) =>
                s.href ? (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => setShareOpen(false)} data-testid={`share-${s.label}`}
                    className="flex items-center h-12 px-4 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium hover:bg-[#E2E8F0]">
                    {s.label}
                  </a>
                ) : (
                  <button key={s.label} onClick={s.action} data-testid={`share-${s.label}`}
                    className="flex items-center h-12 px-4 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium hover:bg-[#E2E8F0] text-left">
                    {s.label}
                  </button>
                )
              )}
            </div>
            <p className="text-xs text-[#94A3B8]">Para Instagram, TikTok u otras apps, usa «Copiar enlace» y pégalo allí.</p>
          </div>
        </div>
      )}
    </div>
  );
}
